import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  LinearProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

const API_BASE = "/api/repricing";

const riskColor = (risk) => {
  if (risk === "low") return "success";
  if (risk === "medium" || risk === "low_medium") return "warning";
  if (risk === "medium_high") return "error";
  return "default";
};

const getInitialQuery = () => {
  const params = new URLSearchParams(window.location.search || "");
  return {
    mode: params.get("repricing_mode") || params.get("mode") || "normal_weekly",
    poId: params.get("po_id") || params.get("purchase_order_id") || "",
    source: params.get("source") || "repricing",
  };
};

export default function RepricingCampaignModes({ onRunCreated, onOpenTab }) {
  const [modes, setModes] = useState([]);
  const [selectedMode, setSelectedMode] = useState(getInitialQuery().mode);
  const [purchaseOrderId, setPurchaseOrderId] = useState(getInitialQuery().poId);
  const [source, setSource] = useState(getInitialQuery().source);
  const [loading, setLoading] = useState(false);
  const [creatingKey, setCreatingKey] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const loadModes = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE}/run-modes`);
      const data = await res.json();
      if (!res.ok || !data.ok) {
        throw new Error(data.error || "Failed to load repricing campaign modes");
      }
      const nextModes = Array.isArray(data.modes) ? data.modes : [];
      setModes(nextModes);
      if (!nextModes.some((mode) => mode.key === selectedMode)) {
        setSelectedMode(nextModes[0]?.key || "normal_weekly");
      }
    } catch (e) {
      setModes([]);
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [selectedMode]);

  useEffect(() => {
    loadModes();
  }, [loadModes]);

  const selected = useMemo(
    () => modes.find((mode) => mode.key === selectedMode) || modes[0] || null,
    [modes, selectedMode]
  );

  const buildPayload = (mode) => {
    const filters = { ...(mode.default_filters || {}) };
    if (purchaseOrderId) {
      filters.purchase_order_id = Number(purchaseOrderId);
      filters.requires_purchase_order = true;
      filters.include_blocked = true;
      if (mode.key === "po_cleanup") {
        delete filters.po_stage;
      }
    }
    return {
      mode: mode.key,
      filters_json: filters,
      rules_json: {
        ...(mode.default_rules || {}),
        ...(purchaseOrderId && mode.key === "po_cleanup" ? { include_all_remaining_po_rows: true } : {}),
        source_context: source || "repricing",
      },
      notes: purchaseOrderId
        ? `${mode.label} from ${source || "repricing"} for PO #${purchaseOrderId}`
        : `${mode.label} from ${source || "repricing"}`,
      created_by: "operator",
      limit: 500,
    };
  };

  const createRun = async (mode) => {
    setCreatingKey(mode.key);
    setNotice("");
    setError("");
    try {
      const res = await fetch(`${API_BASE}/runs`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(buildPayload(mode)),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) {
        throw new Error(data.error || data.message || "Failed to create repricing run");
      }
      setNotice(
        `${data.created ? "Created" : "Opened existing"} ${mode.label} run. ${data.recommendations_persisted || 0} row(s) available for local review.`
      );
      if (onRunCreated) onRunCreated(data);
      if (onOpenTab) onOpenTab("runs", { run_id: data.run?.id });
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setCreatingKey("");
    }
  };

  return (
    <Stack spacing={2}>
      <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} justifyContent="space-between">
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Campaign Modes
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Deterministic presets for weekly repricing. Creating a run persists local recommendation rows only; it does not change listings or eBay.
          </Typography>
        </Box>
        <Button variant="outlined" size="small" onClick={loadModes} disabled={loading}>
          Refresh Modes
        </Button>
      </Stack>

      {loading && <LinearProgress />}
      {error && <Alert severity="error">{error}</Alert>}
      {notice && <Alert severity="success">{notice}</Alert>}

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems={{ xs: "stretch", md: "center" }}>
          <TextField
            size="small"
            label="PO filter (optional)"
            value={purchaseOrderId}
            onChange={(event) => setPurchaseOrderId(event.target.value.replace(/[^0-9]/g, ""))}
            helperText="Used by PO Cleanup / Capital Recovery links. Purchase Orders stay read-only."
          />
          <TextField
            size="small"
            label="Source context"
            value={source}
            onChange={(event) => setSource(event.target.value)}
            helperText="Example: purchase_orders or ebay_intelligence_action_queue"
            sx={{ minWidth: 300 }}
          />
          {selected && (
            <Button variant="contained" onClick={() => createRun(selected)} disabled={Boolean(creatingKey)}>
              {creatingKey === selected.key ? "Creating..." : `Create ${selected.label}`}
            </Button>
          )}
        </Stack>
      </Paper>

      <Grid container spacing={2}>
        {modes.map((mode) => (
          <Grid item xs={12} md={6} xl={4} key={mode.key}>
            <Card
              variant="outlined"
              sx={{
                height: "100%",
                borderColor: selectedMode === mode.key ? "primary.main" : "divider",
              }}
            >
              <CardContent>
                <Stack spacing={1.25}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      {mode.label}
                    </Typography>
                    <Chip size="small" color={riskColor(mode.risk_level)} label={mode.risk_level || "guarded"} />
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    {mode.description}
                  </Typography>
                  {mode.operator_prompt && <Alert severity="info">{mode.operator_prompt}</Alert>}
                  <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                    {(mode.best_for || []).slice(0, 4).map((item) => (
                      <Chip key={item} size="small" variant="outlined" label={item} />
                    ))}
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    Sources: {(mode.cross_module_sources || []).join(" / ") || "Repricing"}
                  </Typography>
                  <Stack direction="row" spacing={1}>
                    <Button size="small" variant="outlined" onClick={() => setSelectedMode(mode.key)}>
                      Select
                    </Button>
                    <Button size="small" variant="contained" onClick={() => createRun(mode)} disabled={Boolean(creatingKey)}>
                      {creatingKey === mode.key ? "Creating..." : "Create Run"}
                    </Button>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Stack>
  );
}
